import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const TestGenerate = () => {
  const { serviceId } = useParams();
  const [answers, setAnswers] = useState({});
  const [generating, setGenerating] = useState(false);
  const [generationId, setGenerationId] = useState(null);
  const [error, setError] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const generateTestDocument = async () => {
    setGenerating(true);
    setError('');

    try {
      const response = await axios.post(`${API}/api/test-account/generate-document`, {
        service_id: parseInt(serviceId),
        answers: answers
      });

      console.log('Generation response:', response.data);
      setGenerationId(response.data.generation_id);
      alert('Document generated successfully! Click Download to get your PDF.');
    } catch (error) {
      console.error('Generation error:', error);
      setError(error.response?.data?.detail || 'Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  const downloadDocument = async () => {
    if (!generationId) {
      alert('Please generate a document first');
      return;
    }

    try {
      const response = await axios.get(`${API}/api/test-account/download/${generationId}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TestDocument_${generationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      alert('Document downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download document');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              🧪 Test Account Document Generation
            </CardTitle>
            <p className="text-center text-gray-600">
              Service ID: {serviceId} | No authentication required for testing
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Basic Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <Input
                    type="text"
                    value={answers['1'] || ''}
                    onChange={(e) => handleInputChange('1', e.target.value)}
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <Input
                    type="text"
                    value={answers['2'] || 'California'}
                    onChange={(e) => handleInputChange('2', e.target.value)}
                    placeholder="Enter your state"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={answers['3'] || ''}
                    onChange={(e) => handleInputChange('3', e.target.value)}
                    placeholder="Enter your email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="text"
                    value={answers['4'] || ''}
                    onChange={(e) => handleInputChange('4', e.target.value)}
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              {/* Additional Test Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <Input
                  type="text"
                  value={answers['5'] || ''}
                  onChange={(e) => handleInputChange('5', e.target.value)}
                  placeholder="Enter your address"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600">{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-4">
                <Button
                  onClick={generateTestDocument}
                  disabled={generating}
                  className="flex-1 bg-teal-500 hover:bg-teal-600"
                >
                  {generating ? 'Generating...' : 'Generate Test Document'}
                </Button>
                
                {generationId && (
                  <Button
                    onClick={downloadDocument}
                    variant="outline"
                    className="flex-1"
                  >
                    Download PDF
                  </Button>
                )}
              </div>

              {generationId && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-600">
                    ✅ Document generated successfully! 
                    <br />
                    Generation ID: {generationId}
                    <br />
                    Click "Download PDF" to get your document.
                  </p>
                </div>
              )}

              {/* Test Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Test Account Features:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• No authentication required</li>
                  <li>• No payment required</li>
                  <li>• No watermarks on documents</li>
                  <li>• Enterprise-level document generation</li>
                  <li>• Instant PDF download</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TestGenerate;